import { ReactNode } from "react";
import { Em } from "@radix-ui/themes";
import { AxisDomain } from "recharts/types/util/types";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { StatusFormatter } from "@/v2/components/formatters/StatusFormatter";
import { ChannelFormatter } from "@/v2/components/formatters/ChannelFormatter";
import { CategoryFormatter } from "@/v2/components/formatters/CategoryFormatter";
import {
  ExploreAggColumnType,
  ExploreColumnType,
  ExploreGroupByColumnType,
  exploreGroupByToColumn,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  formatString as topicFormatString,
  TopicFormatter,
} from "@/v2/components/formatters/TopicFormatter";
import { Skeleton } from "@/components/ui/skeleton";
import { EscalationReasonFormatter } from "@/v2/entities/explore/formatters/EscalationReasonFormatter";
import { ResolutionStatusFormatter } from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";
import { SkipReasonFormatter } from "@/v2/entities/explore/formatters/SkipReasonFormatter";
import { ResolvedReasonFormatter } from "@/v2/entities/explore/formatters/ResolvedReasonFormatter";
import { QuackAnsweredFormatter } from "@/v2/entities/explore/formatters/QuackAnsweredFormatter";
import { IsAnsweredByAuiFormatter } from "@/v2/entities/explore/formatters/IsAnsweredByAuiFormatter";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { AvgQuackMessageScoreFormatter } from "@/v2/components/formatters/AvgQuackMessageScoreFormatter";
import { NumberFormatter } from "@/v2/components/formatters/NumberFormatter";
import { AvgSentimentFormatter } from "@/v2/entities/explore/formatters/AvgSentimentFormatter";
import { TimeToFirstResponseFormatter } from "@/v2/entities/explore/formatters/TimeToFirstResponseFormatter";
import { TimeToResolutionFormatter } from "@/v2/entities/explore/formatters/TimeToResolutionFormatter";
import { CSATFormatter } from "@/v2/components/formatters/CSATFormatter";
import { formatQuestionTitle } from "../qa-score/TicketScore";

const emptyPlaceholder: Partial<Record<ExploreColumnType, string>> = {
  agentAssigned: "Unknown Agent",
  team: "Unknown Team",
  category: "Uncategorized",
  avgQaScore: "Not Scored",
  avgQaScoreRoundRange: "Not Scored",
};

export function getEmptyPlaceholder(column: ExploreColumnType) {
  return emptyPlaceholder[column] ?? "Unclassified";
}

export const CHART_UNCLASSIFIED_LABEL = "Unclassified";

export const normalizeValue = (
  value?: string | number | boolean | null
): string => {
  if (value == null) {
    return "unknown";
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (typeof value === "boolean") {
    return value.toString();
  }

  return (
    value
      ?.replaceAll(" ", "_")
      .replaceAll(/[^a-zA-Z0-9-_]/g, "")
      .toLowerCase() ?? "unknown"
  );
};

const columnToLabel: Record<
  ExploreColumnType,
  ((value: string) => ReactNode) | null
> = {
  tenantId: null,
  id: null,
  displayId: null,
  subject: null,
  description: null,
  createdAt: null,
  updatedAt: null,
  status: (value) => <StatusFormatter value={value} variant="text" />,
  channel: (value) => <ChannelFormatter value={value} />,
  topic: (value) => <>{topicFormatString(value)}</>,
  subTopic: null,
  category: (value) => <CategoryFormatter value={value} />,
  agentAssigned: null,
  team: null,
  teams: null,
  tenantUser: null,
  tenantOrganization: null,
  tags: null,
  avgQaScore: (value) => <AvgQaScoreFormatter value={+value} />,
  avgQaScoreRoundRange: (value) => <AvgQaScoreFormatter value={+value} />,
  avgQuackMessageScoreRoundRange: (value) => (
    <AvgQuackMessageScoreFormatter value={+value} />
  ),
  avgAutoQaScore: (value) => <AvgQaScoreFormatter value={+value} />,
  avgSentiment: (value) => <AvgSentimentFormatter value={+value} />,
  lastSentiment: (value) => <AvgSentimentFormatter value={+value} />,
  avgQuackMessageScore: null,
  csat: null,
  csatComment: null,
  countPublicMessages: null,
  countInternalMessages: null,
  timeToResolution: null,
  timeToFirstResponse: null,
  resolutionStatus: (value) => <ResolutionStatusFormatter value={value} />,
  escalationReason: (value) => <EscalationReasonFormatter value={value} />,
  skipReason: (value) => <SkipReasonFormatter value={value} />,
  resolvedReason: (value) => <ResolvedReasonFormatter value={value} />,
  countQuackMessages: null,
  isQuackEscalated: null,
  isQuackAnswered: (value) => {
    return <QuackAnsweredFormatter value={value === "true"} />;
  },
  isAnsweredByAui: (value) => (
    <IsAnsweredByAuiFormatter value={value === "true"} />
  ),
  collaborators: null,
  isFiniteState: null,
  validationStatus: null,
  agentAssignedId: null,
  ticketId: null,
  originAuthor: null,
  originUrl: null,
  scoreTitle: (value) => <>{formatQuestionTitle(value)}</>,
  scoreContent: null,
  simulatorId: null,
  skillUsed: null,
  skillsUsed: null,
  isUserAuthenticated: null,
  avgCsat: null,
};

const getColumnLabel = (column: ExploreColumnType) => {
  return (value?: string) => {
    if (value == null) {
      return <Em>{getEmptyPlaceholder(column)}</Em>;
    }

    return columnToLabel[column]?.(value) ?? value;
  };
};

const columnToColor: Record<ExploreColumnType, Record<string, string> | null> =
  {
    tenantId: null,
    id: null,
    displayId: null,
    subject: null,
    description: null,
    createdAt: null,
    updatedAt: null,
    status: null,
    channel: null,
    topic: null,
    subTopic: null,
    category: null,
    agentAssigned: null,
    team: null,
    teams: null,
    tenantUser: null,
    tenantOrganization: null,
    tags: null,
    avgQaScore: null,
    avgQaScoreRoundRange: null,
    avgQuackMessageScoreRoundRange: null,
    avgAutoQaScore: null,
    avgSentiment: null,
    lastSentiment: null,
    avgQuackMessageScore: null,
    csat: null,
    csatComment: null,
    countPublicMessages: null,
    countInternalMessages: null,
    timeToResolution: null,
    timeToFirstResponse: null,
    // TODO: Add color overrides for resolution status, escalation reason, skip reason, and resolved reason
    resolutionStatus: {
      [normalizeValue("RESOLVED")]: "var(--green-a10)",
      [normalizeValue("NOT_RESOLVED")]: "var(--gray-a6)",
      [normalizeValue("EXPECTED_ESCALATION")]: "var(--yellow-a10)",
      [normalizeValue("UNEXPECTED_ESCALATION")]: "var(--red-a10)",
      [normalizeValue("SKIPPED")]: "var(--orange-a10)",
    },
    escalationReason: null,
    skipReason: null,
    resolvedReason: null,
    countQuackMessages: null,
    isQuackEscalated: null,
    isQuackAnswered: {
      true: "var(--green-a10)",
      false: "var(--gray-a6)",
    },
    isAnsweredByAui: {
      true: "var(--green-a10)",
      false: "var(--gray-a6)",
    },
    collaborators: null,
    isFiniteState: null,
    validationStatus: null,
    agentAssignedId: null,
    ticketId: null,
    originAuthor: null,
    originUrl: null,
    scoreTitle: null,
    scoreContent: null,
    simulatorId: null,
    skillUsed: null,
    skillsUsed: null,
    isUserAuthenticated: null,
    avgCsat: null,
  };

export function getColumnConfig(column: ExploreColumnType) {
  return {
    labelFormatter: getColumnLabel(column),
    colorOverrides: columnToColor[column] ?? undefined,
  };
}

export function ExploreChartLoader() {
  return <Skeleton className="h-full w-full" />;
}

export const exploreChartEmptyStateVariants = ["no-data", "no-metric"] as const;
type ExploreChartEmptyStateVariant =
  (typeof exploreChartEmptyStateVariants)[number];

const exploreChartEmptyStateContent: Record<
  ExploreChartEmptyStateVariant,
  { title: string; description: string }
> = {
  "no-data": {
    title: "No Data",
    description: "Try different filters or time range",
  },
  "no-metric": {
    title: "No Metric Selected",
    description: "Choose a metric to view its data distribution.",
  },
};

interface ExploreChartEmptyStateProps {
  variant: ExploreChartEmptyStateVariant;
}

export function ExploreChartEmptyState({
  variant,
}: ExploreChartEmptyStateProps) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-2">
      <div className="text-2xl font-bold">
        {exploreChartEmptyStateContent[variant].title}
      </div>
      <div className="text-muted-foreground">
        {exploreChartEmptyStateContent[variant].description}
      </div>
    </div>
  );
}

const aggColumnToValueFormatter: Record<
  ExploreAggColumnType,
  ((value: number) => ReactNode) | null
> = {
  volume: (value) => <NumberFormatter value={value} />,
  volumeRatio: (value) => <RatioFormatter value={value} />,
  avgQuackMessages: (value) => <NumberFormatter value={value} />,
  avgQaScore: (value) => <AvgQaScoreFormatter value={value} />,
  avgAutoQaScore: (value) => <AvgQaScoreFormatter value={value} />,
  avgSentiment: (value) => <AvgSentimentFormatter value={value} />,
  avgCsat: (value) => <NumberFormatter value={value} />,
  avgPublicMessages: (value) => <NumberFormatter value={value} />,
  avgInternalMessages: (value) => <NumberFormatter value={value} />,
  countAgentsAssigned: (value) => <NumberFormatter value={value} />,
  avgTimeToResolution: (value) => (
    <TimeToResolutionFormatter shorthand value={value} />
  ),
  avgTimeToFirstResponse: (value) => (
    <TimeToFirstResponseFormatter shorthand value={value} />
  ),
  avgQaScoreByTitle: (value) => <AvgQaScoreFormatter value={value} />,
  avgQaScoreByContent: (value) => <AvgQaScoreFormatter value={value} />,
};

const aggColumnDomain: Record<ExploreAggColumnType, AxisDomain | null> = {
  volume: null,
  volumeRatio: null,
  avgQuackMessages: null,
  avgQaScore: [1, 5],
  avgAutoQaScore: [1, 5],
  avgSentiment: [-1, 1],
  avgCsat: [0, 5],
  avgPublicMessages: null,
  avgInternalMessages: null,
  countAgentsAssigned: null,
  avgTimeToResolution: null,
  avgTimeToFirstResponse: null,
  avgQaScoreByTitle: [1, 5],
  avgQaScoreByContent: [1, 5],
};

const aggColumnFooterValueFormatter: Record<
  ExploreAggColumnType,
  ((value: number) => ReactNode) | null
> = {
  volume: (value) => <NumberFormatter value={value} />,
  volumeRatio: (value) => <NumberFormatter value={value} />,
  avgQuackMessages: (value) => <NumberFormatter value={value} />,
  avgQaScore: (value) => <AvgQaScoreFormatter value={value} />,
  avgAutoQaScore: (value) => <AvgQaScoreFormatter value={value} />,
  avgSentiment: (value) => <AvgSentimentFormatter value={value} />,
  avgCsat: (value) => <NumberFormatter value={value} />,
  avgPublicMessages: (value) => <NumberFormatter value={value} />,
  avgInternalMessages: (value) => <NumberFormatter value={value} />,
  countAgentsAssigned: (value) => <NumberFormatter value={value} />,
  avgTimeToResolution: (value) => (
    <TimeToResolutionFormatter shorthand value={value} />
  ),
  avgTimeToFirstResponse: (value) => (
    <TimeToFirstResponseFormatter shorthand value={value} />
  ),
  avgQaScoreByTitle: (value) => <AvgQaScoreFormatter value={value} />,
  avgQaScoreByContent: (value) => <AvgQaScoreFormatter value={value} />,
};

const aggColumnFooterLabel: Record<ExploreAggColumnType, string | null> = {
  volume: "Total",
  volumeRatio: "Total",
  avgQuackMessages: "Avg.",
  avgQaScore: "Avg.",
  avgAutoQaScore: "Avg.",
  avgSentiment: "Avg.",
  avgCsat: "Avg.",
  avgPublicMessages: "Avg.",
  avgInternalMessages: "Avg.",
  countAgentsAssigned: "Avg.",
  avgTimeToResolution: "Avg.",
  avgTimeToFirstResponse: "Avg.",
  avgQaScoreByTitle: "Avg.",
  avgQaScoreByContent: "Avg.",
};

export function getAggColumnConfig(type: ExploreAggColumnType) {
  const valueFormatter = aggColumnToValueFormatter[type] ?? undefined;
  const labelFormatter = (value: string) => {
    return valueFormatter?.(+value) ?? value;
  };

  return {
    valueFormatter,
    labelFormatter,
    footerValueFormatter: aggColumnFooterValueFormatter[type] ?? undefined,
    footerLabel: aggColumnFooterLabel[type] ?? undefined,
    domain: aggColumnDomain[type] ?? undefined,
    shouldStack: type === "volume",
  };
}

// ExploreGroupByColumnType
const groupByColumnToLabel: Record<
  ExploreGroupByColumnType,
  ((value: string) => ReactNode) | null
> = {
  tenantId: null,
  status: (value) => <StatusFormatter value={value} variant="text" />,
  channel: (value) => <ChannelFormatter value={value} />,
  topic: (value) => <TopicFormatter value={value} />,
  category: (value) => <CategoryFormatter value={value} />,
  resolutionStatus: (value) => <ResolutionStatusFormatter value={value} />,
  escalationReason: (value) => <EscalationReasonFormatter value={value} />,
  skipReason: (value) => <SkipReasonFormatter value={value} />,
  resolvedReason: (value) => <ResolvedReasonFormatter value={value} />,
  subTopic: null,
  agentAssigned: null,
  team: null,
  week: null,
  day: null,
  month: null,
  avgQaScore: (value) => <AvgQaScoreFormatter value={+value} />,
  avgQaScoreRoundRange: (value) => <AvgQaScoreFormatter value={+value} />,
  avgQuackMessageScoreRoundRange: (value) => (
    <AvgQuackMessageScoreFormatter value={+value} />
  ),
  avgSentiment: (value) => <AvgSentimentFormatter value={+value} />,
  isQuackAnswered: (value) => (
    <QuackAnsweredFormatter value={value === "true"} />
  ),
  isAnsweredByAui: (value) => (
    <IsAnsweredByAuiFormatter value={value === "true"} />
  ),
  originUrl: null,
  scoreTitle: null,
  scoreContent: null,
  simulator: null,
  skillUsed: null,
  avgCsat: (value) => <CSATFormatter value={+value} />,
};

const getGroupByColumnLabel = (column: ExploreGroupByColumnType) => {
  return (value?: string) => {
    if (value == null) {
      if (exploreGroupByToColumn[column] != null) {
        return <Em>{getEmptyPlaceholder(exploreGroupByToColumn[column]!)}</Em>;
      }

      return value;
    }

    return groupByColumnToLabel[column]?.(value) ?? value;
  };
};

const groupByColumnColorOverrides: Record<
  ExploreGroupByColumnType,
  Record<string, string> | null
> = {
  tenantId: null,
  status: null,
  channel: null,
  topic: null,
  category: null,
  resolutionStatus: {
    [normalizeValue("RESOLVED")]: "var(--green-a10)",
    [normalizeValue("NOT_RESOLVED")]: "var(--gray-a6)",
    [normalizeValue("EXPECTED_ESCALATION")]: "var(--yellow-a10)",
    [normalizeValue("UNEXPECTED_ESCALATION")]: "var(--red-a10)",
    [normalizeValue("SKIPPED")]: "var(--orange-a10)",
  },
  escalationReason: null,
  skipReason: null,
  resolvedReason: null,
  subTopic: null,
  agentAssigned: null,
  team: null,
  week: null,
  day: null,
  month: null,
  avgQaScore: null,
  avgQaScoreRoundRange: null,
  avgQuackMessageScoreRoundRange: null,
  avgSentiment: null,
  isQuackAnswered: null,
  isAnsweredByAui: null,
  originUrl: null,
  scoreTitle: null,
  scoreContent: null,
  simulator: null,
  skillUsed: null,
  avgCsat: null,
};

export function getGroupByColumnConfig(column: ExploreGroupByColumnType) {
  return {
    labelFormatter: getGroupByColumnLabel(column),
    colorOverrides: groupByColumnColorOverrides[column] ?? undefined,
  };
}
